import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Upload, X, Check, AlertCircle, Image as ImageIcon, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { analyticsService } from '@/services/analytics'

interface Coupon {
  id: string
  imageUrl?: string
  uploadDate: string
  status: 'pending' | 'approved' | 'rejected'
  amount?: number
  description?: string
}

export function CouponPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [uploadData, setUploadData] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    // Track page view
    analyticsService.trackScreenView('coupon_page')

    // Load coupons from localStorage for demo
    const savedCoupons = localStorage.getItem('coupons')
    if (savedCoupons) {
      setCoupons(JSON.parse(savedCoupons))
      // Track number of coupons loaded
      analyticsService.trackEvent('coupons_loaded', {
        coupon_count: JSON.parse(savedCoupons).length,
        timestamp: new Date().toISOString(),
      })
    }
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadData(file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = (e) => {
        setPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!uploadData) {
      toast.error('Veuillez sélectionner une image')
      return
    }

    setIsLoading(true)
    const uploadStartTime = Date.now()

    try {
      // Track upload start
      analyticsService.trackEvent('coupon_upload_started', {
        file_size: uploadData.size,
        file_type: uploadData.type,
        timestamp: new Date().toISOString(),
      })

      // Simulate upload
      await new Promise(resolve => setTimeout(resolve, 2000))

      const newCoupon: Coupon = {
        id: Date.now().toString(),
        imageUrl: preview || undefined,
        uploadDate: new Date().toLocaleString('fr-FR'),
        status: 'pending',
        amount: Math.floor(Math.random() * 5000) + 1000, // Random amount for demo
        description: `Coupon uploadé le ${new Date().toLocaleString('fr-FR')}`
      }

      const updatedCoupons = [newCoupon, ...coupons]
      setCoupons(updatedCoupons)

      // Save to localStorage
      localStorage.setItem('coupons', JSON.stringify(updatedCoupons))

      // Track successful upload with coupon generation tracking
      analyticsService.trackCouponGenerated({
        coupon_code: newCoupon.id,
        discount_value: newCoupon.amount || 0,
      })

      analyticsService.trackEvent('coupon_upload_success', {
        coupon_id: newCoupon.id,
        upload_duration_ms: Date.now() - uploadStartTime,
        coupon_amount: newCoupon.amount,
        total_coupons_count: updatedCoupons.length,
        timestamp: new Date().toISOString(),
      })

      toast.success('Coupon téléchargé avec succès!', {
        description: 'Il sera traité dans les plus brefs délais.'
      })

      // Reset form
      setUploadData(null)
      setPreview(null)
      const form = e.target as HTMLFormElement
      form.reset()

    } catch (error) {
      console.error('Error uploading coupon:', error)

      // Track upload error
      analyticsService.trackError(error as Error, 'coupon_upload')
      analyticsService.trackEvent('coupon_upload_failed', {
        upload_duration_ms: Date.now() - uploadStartTime,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      })

      toast.error('Erreur lors du téléchargement du coupon. Veuillez réessayer.')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente'
      case 'approved':
        return 'Approuvé'
      case 'rejected':
        return 'Rejeté'
      default:
        return 'Inconnu'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />
      case 'approved':
        return <Check className="w-4 h-4" />
      case 'rejected':
        return <X className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="mr-3"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-semibold text-gray-900">
            Mes Coupons
          </h1>
          {coupons.length > 0 && (
            <div className="ml-auto">
              <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium">
                {coupons.length} coupon{coupons.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Télécharger un coupon</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              {/* Preview */}
              {preview ? (
                <div className="relative">
                  <img
                    src={preview}
                    alt="Aperçu du coupon"
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setPreview(null)
                      setUploadData(null)
                    }}
                    className="absolute top-2 right-2 bg-card hover:bg-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <div>
                    <Label htmlFor="coupon-upload" className="cursor-pointer">
                      <span className="text-[#002366] hover:text-[#0052cc] font-medium">
                        Cliquez pour sélectionner une image
                      </span>
                      <Input
                        id="coupon-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={isLoading}
                      />
                    </Label>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Formats supportés: JPG, PNG, GIF (max 5MB)
                  </p>
                </div>
              )}

              <Button
                type="submit"
                disabled={!uploadData || isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-foreground font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Téléchargement...' : 'Télécharger le coupon'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Coupons List */}
        {coupons.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Historique</h2>
            {coupons.map((coupon) => (
              <Card key={coupon.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    {/* Coupon Image */}
                    <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {coupon.imageUrl ? (
                        <img
                          src={coupon.imageUrl}
                          alt="Coupon"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Coupon Info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(coupon.status)}`}>
                          {getStatusIcon(coupon.status)}
                          <span>{getStatusText(coupon.status)}</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {coupon.uploadDate}
                        </span>
                      </div>

                      {coupon.amount && (
                        <div className="text-sm font-medium text-gray-900">
                          Valeur: {coupon.amount.toLocaleString('fr-FR')} FCFA
                        </div>
                      )}

                      {coupon.description && (
                        <div className="text-sm text-gray-600">
                          {coupon.description}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {coupons.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun coupon téléchargé
              </h3>
              <p className="text-gray-500 text-sm">
                Téléchargez vos premiers coupons pour commencer à bénéficier des avantages
              </p>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Comment utiliser les coupons</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-2">
              <span className="text-[#002366] font-bold">1.</span>
              <p className="text-sm text-gray-600">
                Prenez une photo claire de votre coupon
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-[#002366] font-bold">2.</span>
              <p className="text-sm text-gray-600">
                Téléchargez-le via cette page
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-[#002366] font-bold">3.</span>
              <p className="text-sm text-gray-600">
                Attendez la validation manuelle
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-[#002366] font-bold">4.</span>
              <p className="text-sm text-gray-600">
                Utilisez le bonus une fois approuvé
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Support Info */}
        <Alert>
          <AlertDescription className="text-sm">
            Les coupons sont validés manuellement par notre équipe. Contactez le support pour toute question.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  )
}

export default CouponPage